import {
  Briefcase,
  Calculator,
  ShieldCheck,
  Code2,
  GraduationCap,
  Rocket,
  Megaphone,
} from "lucide-react";

export const services = {
  "strategy-operations": {
    id: "strategy-operations",
    slug: "business-strategy-operations",
    title: "Business Strategy & Operations",
    icon: "Briefcase", // Keeping as string to match your dynamic loading logic
    color: "blue",
    tagline:
      "Translating strategy into measurable, sustainable outcomes at every level.",
    heroDescription:
      "We bridge the gap between high-level vision and daily execution. Our approach embeds a culture of accountability and continuous improvement to build resilient operating models that scale with your enterprise.",
    sections: [
      {
        title: "Market & Growth Strategy",
        content:
          "Formulation of data-driven strategic growth plans that identify high-value market opportunities, assess competitive positioning, and define clear pathways for commercial expansion. Encompasses rigorous market research and analysis, competitor benchmarking, business expansion planning, and new market entry strategy.",
      },
      {
        title: "Process Optimisation & Efficiency",
        content:
          "Comprehensive operational audits and workflow redesign engagements that systematically identify and eliminate process bottlenecks, redundancies, and inefficiencies across all business functions to reduce operating costs and maximise productivity.",
      },
      {
        title: "Organisational Design & Governance",
        content:
          "Advisory support in architecting robust organisational structures, governance frameworks, and accountability mechanisms aligned to strategic objectives. We help define clear reporting lines and operating protocols that promote agility and transparency.",
      },
      {
        title: "Performance Management Systems",
        content:
          "Design and implementation of structured performance management frameworks—including OKRs, KPIs, and Balanced Scorecards—that align individual, team, and organisational objectives with measurable business outcomes.",
      },
      {
        title: "Change Management & Business Transformation",
        content:
          "Expert-led guidance for organisations navigating high-stakes business transitions—including M&A, digital transformation, corporate restructuring, and operational re-engineering, ensuring seamless execution and stakeholder alignment.",
      },
    ],
    process: [
      "Operational Audit",
      "Strategy Formulation",
      "System Implementation",
      "Performance Governance",
    ],
    features: [
      "Market Research & Analysis",
      "Competitor Benchmarking",
      "Workflow Audit & Bottleneck Elimination",
      "OKR & KPI Frameworks",
      "M&A Integration Support",
    ],
    benefits: [
      "Leaner Operating Models",
      "Increased Productivity",
      "Scalable Governance",
      "Strategic Agility",
    ],
  },

  "finance-tax": {
    id: "finance-tax",
    slug: "financial-advisory-tax-planning",
    title: "Financial Advisory & Tax Planning",
    icon: "Calculator",
    color: "emerald",
    tagline:
      "Comprehensive financial, taxation, compliance, and advisory services.",
    heroDescription:
      "From early-stage bookkeeping to complex statutory audits and wealth management, we provide the financial clarity required for data-driven decisions and regulatory peace of mind.",
    sections: [
      {
        title: "Accounting & Bookkeeping Services",
        content:
          "End-to-end maintenance of books of accounts under the double-entry system, with preparation of financial statements (P&L, Balance Sheet, Cash Flow) compliant with Ind AS and IGAAP. Includes payroll processing, bank reconciliation, and MIS reporting.",
      },
      {
        title: "Taxation & Compliance Services",
        content:
          "Comprehensive direct and indirect tax advisory covering ITR filing, GST registration and periodic returns, TDS/TCS compliance, advance tax computation, transfer pricing studies, and FEMA/RBI filings.",
      },
      {
        title: "Statutory & Legal Compliance",
        content:
          "End-to-end company and LLP incorporation under the Companies Act 2013, ROC annual filings, maintenance of statutory registers, PF/ESIC/PT registrations, trade licences, MSME/Udyam, and drafting of shareholder agreements.",
      },
      {
        title: "Audit & Assurance Services",
        content:
          "Statutory audit, tax audit (Section 44AB), internal/concurrent audits, GST audit with GSTR-9C certification, forensic investigation, and comprehensive financial due diligence for M&A transactions.",
      },
      {
        title: "Investment & Wealth Advisory",
        content:
          "Goal-oriented financial planning across mutual funds, equity, and fixed income. Encompasses retirement planning, NRI investment advisory, estate planning, HUF structuring, and succession advisory.",
      },
      {
        title: "Business & Corporate Advisory",
        content:
          "Strategic corporate advisory encompassing business plan development, fundraising strategy, debt syndication, business valuation (DCF, NAV), Virtual CFO services, and advisory under IBC 2016 for NCLT representation.",
      },
    ],
    process: [
      "Compliance Mapping",
      "Financial Structuring",
      "Audit & Review",
      "Strategic Tax Planning",
    ],
    features: [
      "Ind AS / IGAAP Compliance",
      "GST & ITR Filing",
      "ROC Filings & Secretarial",
      "Forensic Investigation",
      "Virtual CFO Services",
    ],
    benefits: [
      "Zero-Error Compliance",
      "Optimized Tax Outflow",
      "Investor Readiness",
      "Improved Cash Flow",
    ],
  },

  cybersecurity: {
    id: "cybersecurity",
    slug: "cybersecurity-framework",
    title: "Cybersecurity Framework",
    icon: "ShieldCheck",
    color: "red",
    tagline:
      "Building secure digital foundations from inception through scale.",
    heroDescription:
      "We protect your digital assets by embedding security-by-design principles into your technology lifecycle, ensuring resilience against evolving global threats and full compliance with industry standards.",
    sections: [
      {
        title: "Security Advisory During Startup Incubation",
        content:
          "Strategic cybersecurity guidance delivered during the incubation phase, enabling startups to engineer a resilient technology foundation from day one. Includes architecture review and secure system design.",
      },
      {
        title: "Secure Product Development Guidance",
        content:
          "Embedding security-by-design principles throughout the software development lifecycle (SDLC) to help teams deliver robust, vulnerability-resistant applications with integrated CI/CD security controls.",
      },
      {
        title: "Security Testing & Vulnerability Assessment",
        content:
          "Rigorous pre-launch security testing to uncover and remediate vulnerabilities across digital platforms. Encompasses penetration testing, web app and API security assessment against OWASP frameworks.",
      },
      {
        title: "Cloud & Infrastructure Security Review",
        content:
          "Thorough evaluation of cloud-hosted environments ensuring configurations, IAM access controls, and network architecture adhere to established standards to prevent sensitive data exposure.",
      },
      {
        title: "Governance, Risk & Compliance (GRC) Support",
        content:
          "Guidance to align security practices with recognised frameworks, data protection regulations, and industry compliance requirements. Includes structured risk assessments and audit preparation.",
      },
      {
        title: "Security Awareness & Workforce Development",
        content:
          "Targeted initiatives to equip the workforce with knowledge to identify threats (phishing, social engineering), safeguard information, and uphold a security-conscious organisational culture.",
      },
      {
        title: "Periodic Security Reviews",
        content:
          "Ongoing security review engagements calibrated to the organisation's evolving operational scale—ensuring security posture remains effective and adaptive to emerging threats.",
      },
    ],
    process: [
      "Architecture Review",
      "Vulnerability Discovery",
      "Remediation Guidance",
      "Continuous Monitoring",
    ],
    features: [
      "Pre-launch Validation",
      "Penetration Testing & API Security",
      "Cloud Hardening",
      "Compliance Gap Analysis",
      "Phishing Awareness",
    ],
    benefits: [
      "Reduced Cyber Risk",
      "Framework Alignment",
      "Data Integrity",
      "Workforce Resilience",
    ],
  },

  "software-development": {
    id: "software-development",
    slug: "software-digital-products",
    title: "Software & Digital Products",
    icon: "Code2",
    color: "indigo",
    tagline: "End-to-end engineering of scalable digital platforms.",
    heroDescription:
      "We build bespoke software solutions tailored to your operational requirements, focusing on performance, intuitive UI/UX, and long-term architectural scalability.",
    sections: [
      {
        title: "Technology Consultation & Product Planning",
        content:
          "Strategic advisory to define technology direction prior to development. Covers product concept analysis, tech stack selection, system architecture, MVP scoping, and scalability planning.",
      },
      {
        title: "Web Application Development",
        content:
          "Design and engineering of secure, performant, responsive web platforms. Encompasses UI/UX design, backend engineering, RESTful APIs, and administrative dashboards.",
      },
      {
        title: "Desktop Application Development",
        content:
          "Development of bespoke offline-capable or locally-deployed desktop software solutions for Windows and macOS, including business process automation and data reporting tools.",
      },
      {
        title: "Mobile Application Development",
        content:
          "High-performance native and cross-platform mobile applications for Android and iOS, delivering intuitive user experiences, device compatibility testing, and app store deployment.",
      },
      {
        title: "SaaS Product Development",
        content:
          "End-to-end engineering of Software-as-a-Service platforms architected for multi-tenancy, subscription billing integration, cloud provisioning, and scalable commercial growth.",
      },
      {
        title: "System Integration & Platform Connectivity",
        content:
          "Seamless integration of digital platforms with third-party services, payment gateways, CRM/ERP systems, and cloud storage to ensure unified cross-system data synchronisation.",
      },
      {
        title: "Product Testing, Deployment & Maintenance",
        content:
          "Structured quality assurance, go-live deployment, functional testing, performance assessment, and ongoing post-launch technical support to ensure operational continuity.",
      },
    ],
    process: [
      "Tech Consultation",
      "Prototyping & UX",
      "Agile Development",
      "QA & Deployment",
    ],
    features: [
      "MVP Scoping & Architecture",
      "Responsive UI/UX Design",
      "Cross-platform Mobile Apps",
      "Multi-tenant SaaS Architecture",
      "API & Payment Integrations",
    ],
    benefits: [
      "Rapid Market Entry",
      "Seamless Interoperability",
      "User-Centric Experience",
      "Future-proof Tech",
    ],
  },

  "training-development": {
    id: "training-development",
    slug: "training-workforce-development",
    title: "Training & Development",
    icon: "GraduationCap",
    color: "amber",
    tagline:
      "Capability-building programs that accelerate workforce performance.",
    heroDescription:
      "Cultivate a future-ready workforce through targeted leadership coaching, technical upskilling, and immersive training platforms tailored to your organisational culture.",
    sections: [
      {
        title: "Leadership & Management Development",
        content:
          "Targeted programs engineered to strengthen executive decision-making, strategic thinking, and people management capabilities for senior leaders and emerging executives.",
      },
      {
        title: "Technical & Professional Skills Training",
        content:
          "Practical, outcomes-focused training that deepens technical knowledge and elevates job-specific competencies, driving measurable improvements in employee productivity.",
      },
      {
        title: "Sales & Customer Experience Training",
        content:
          "Immersive, hands-on programs that refine communication, negotiation, and client engagement competencies—equipping teams to achieve superior commercial outcomes.",
      },
      {
        title: "Corporate Soft Skills & Team Development",
        content:
          "Facilitated workshops addressing emotional intelligence, conflict resolution, and cross-functional collaboration to cultivate high-performing team environments.",
      },
      {
        title: "Employee Onboarding & Workplace Readiness",
        content:
          "Structured frameworks designed to accelerate new employee integration, enabling rapid alignment with organisational culture, processes, and professional standards.",
      },
      {
        title: "Digital Learning & Training Platform Development",
        content:
          "Development of bespoke e-learning solutions and Learning Management Systems (LMS) tailored to deliver scalable, trackable training across distributed workforces.",
      },
      {
        title: "Cybersecurity Training & Workforce Development",
        content:
          "Programs that equip employees to identify cyber threats (phishing, social engineering), adhere to secure practices, and build a resilient security-aware culture.",
      },
    ],
    process: [
      "Gap Analysis",
      "Curriculum Design",
      "Immersive Facilitation",
      "Impact Assessment",
    ],
    features: [
      "Executive Coaching",
      "Sales & Negotiation Skills",
      "Conflict Resolution Workshops",
      "LMS Platform Development",
      "Cybersecurity Awareness",
    ],
    benefits: [
      "High-Performing Teams",
      "Accelerated Onboarding",
      "Skill Retention",
      "Operational Effectiveness",
    ],
  },

  "startup-incubation": {
    id: "startup-incubation",
    slug: "startup-incubation-investment-advisory",
    title: "Startup Incubation & Advisory",
    icon: "Rocket",
    color: "purple",
    tagline:
      "Empowering early-stage ventures to grow with clarity and investor confidence.",
    heroDescription:
      "We guide founders through the critical phases of venture building by providing structural mentorship, financial modeling, and direct access to strategic capital networks.",
    sections: [
      {
        title: "Startup Mentorship & Business Structuring",
        content:
          "Expert-led mentorship guiding founders through refining business models, structuring core operations, and establishing foundations for sustainable long-term growth.",
      },
      {
        title: "Business Plan & Pitch Deck Development",
        content:
          "End-to-end support in crafting investor-grade business plans and persuasive pitch decks that articulate market opportunity and strategic growth with commercial conviction.",
      },
      {
        title: "Funding Strategy & Investor Readiness",
        content:
          "Preparation for external fundraising, encompassing robust financial projection modelling, investment strategy formulation, and rigorous due-diligence readiness.",
      },
      {
        title: "Investor Networking & Capital Access",
        content:
          "Strategic facilitation of connections between startups and a curated network of venture capitalists, angel investors, and institutional funding bodies.",
      },
      {
        title: "Startup Growth & Market Scaling",
        content:
          "Advisory support for transitioning from early traction to scalable growth, including operational capacity development and strategic partnership cultivation.",
      },
      {
        title: "Startup Compliance & Legal Guidance",
        content:
          "Practical guidance navigating regulatory obligations, company registration, and legal frameworks to ensure startups are structurally compliant and operationally protected.",
      },
    ],
    process: [
      "Business Refinement",
      "Financial Modeling",
      "Pitch Preparation",
      "Capital Facilitation",
    ],
    features: [
      "Founder Coaching",
      "Pitch Deck Narrative Design",
      "Financial Projections & Valuations",
      "VC & Angel Network Intros",
      "Company Registration & IP",
    ],
    benefits: [
      "Investor Validation",
      "Structural Resilience",
      "Strategic Networking",
      "Accelerated Scaling",
    ],
  },

  "marketing-sales": {
    id: "marketing-sales",
    slug: "marketing-lead-generation",
    title: "Marketing, Lead Generation & Sales",
    icon: "Megaphone",
    color: "orange",
    tagline: "Integrated commercial growth services elevating brand authority.",
    heroDescription:
      "We systematically transform audience interest into commercially actionable business opportunities through data-driven campaigns, optimised funnels, and robust CRM systems.",
    sections: [
      {
        title: "Marketing Strategy & Brand Positioning",
        content:
          "Formulation of insight-driven strategies that strengthen brand visibility, sharpen positioning, and ensure businesses engage the right audiences with the right message.",
      },
      {
        title: "Digital Marketing & Campaign Management",
        content:
          "End-to-end planning of data-driven campaigns across search, social, and content platforms—optimised to generate a consistent pipeline of high-intent leads.",
      },
      {
        title: "Lead Generation Systems",
        content:
          "Design of structured, multi-channel lead generation systems that attract, capture, and qualify prospects through targeted inbound and outbound marketing.",
      },
      {
        title: "Sales Funnel & Conversion Optimisation",
        content:
          "Strategic design and A/B testing of sales funnels that guide prospects through the buying journey with precision, reducing drop-off and improving conversion rates.",
      },
      {
        title: "CRM & Sales Process Implementation",
        content:
          "Implementation of enterprise-grade CRM platforms and structured sales frameworks to manage lead pipelines, track interactions, and improve sales team performance.",
      },
      {
        title: "Customer Retention & Growth Strategies",
        content:
          "Development of targeted engagement programs that deepen client relationships, increase repeat purchase behaviour, and transform satisfied customers into loyal advocates.",
      },
    ],
    process: [
      "Brand Audit",
      "Funnel Architecture",
      "Campaign Execution",
      "Conversion Tracking",
    ],
    features: [
      "Audience Targeting & Positioning",
      "PPC, SEO & Social Media",
      "Inbound/Outbound Lead Capture",
      "A/B Testing & Funnel Design",
      "CRM Implementation",
    ],
    benefits: [
      "Consistent Lead Pipeline",
      "Higher ROI on Ads",
      "Improved Sales Conversion",
      "Brand Loyalty & Advocacy",
    ],
  },
};

export const testimonials = [
  {
    id: 1,
    name: "Muhammed Jasin K T",
    position: "Director",
    company: "IMAAR Tours & Travels",
    content:
      "“Our previous team was bleeding money — unnecessary government fees, inflated tax payments, and zero clarity. Then Maroons stepped in, and everything changed.” Maroons’ CFO, Sarath, took complete charge of our financial department. He identified where we were overspending, restructured our tax planning, and helped us save lakhs — legally and efficiently. Today, every legal notification is handled, every compliance is met, and our entire financial operation runs seamlessly under Maroons’ watch. What once felt like a burden is now a system that gives us complete peace of mind. Maroons didn’t just fix our finances — they secured our future.",
    rating: 5,
    tags: ["Tax Savings", "Legal Compliance", "CFO Support"],
    photo:
      "https://res.cloudinary.com/dkoljvhc9/image/upload/v1777481329/Mohammed_pr1bes.jpg",
  },
  {
    id: 2,
    name: "Sambhu Sreekumaran Nair",
    position: "Founder",
    company: "RVS Venture Pvt Ltd",
    content:
      "“I came to Maroons with just an idea — rough, unpolished, and unsure of where to start. From the moment I registered, their team took full ownership. They didn’t just build what I asked for; they delivered something far beyond what I imagined. What I expected was a product. What I got was a launchpad.” Maroons transformed my concept into a fully functional startup — on time, with precision, and with a level of quality that genuinely surprised me. If you have an idea and need a team that treats it like their own, look no further.",
    rating: 5,
    tags: ["Product Development", "Startup Launchpad"],
    photo:
      "https://res.cloudinary.com/dkoljvhc9/image/upload/v1777483277/Sambhu_Sreekumar_wagjyi.jpg",
  },
  {
    id: 3,
    name: "Abdul Sathar A",
    position: "Founder",
    company: "Pure Vedic LLP",
    content:
      "“I came to Maroons with a limited budget and a big dream. They welcomed me, supported me, and never once made me feel small for it.” From day one, Maroons didn’t just build my venture — they mapped the entire route. Every step, every stage, they were right there guiding me with clarity and commitment. Today, I’m walking confidently toward my destination — with government grants secured and private funding at final stage. What started as a small step with a low budget has become a fully backed, ready-to-launch business — all because Maroons believed in it as much as I did.",
    rating: 5,
    tags: ["Govt. Grants Secured", "Private Funding", "Ready to Launch"],
    photo:
      "https://res.cloudinary.com/dkoljvhc9/image/upload/v1777481329/Abdul_Sathar_ruseki.jpg",
  },
  {
    id: 4,
    name: "Shany M Salihan",
    position: "Founder",
    company: "Kopichic LLP",
    content:
      "“I walked in as a solo founder with nothing but an idea. Maroons walked me out as a real business — with a product, a plan, and government grants in hand.” They guided me through every single stage — product development, research, technical execution, and grant applications — with patience and precision I never expected. What truly sets Maroons apart is how personally they invest in you. They didn’t just build my startup; they became my family. I started this journey alone. Now I have a team I trust completely, and a future that’s still being built — with Maroons right beside me.",
    rating: 5,
    tags: ["Govt. Grants Secured", "Ongoing Mentorship"],
    photo:
      "https://res.cloudinary.com/dkoljvhc9/image/upload/v1777481329/Shany_M_Salihan_jwnnm7.jpg",
  },
];

export const companyInfo = {
  name: "Maroons Business Solutions",
  tagline: "Everything Gets Solved Here",
  description:
    "Maroons is a collective of business experts strategically positioned across all aspects of enterprise needs. Whether it's branding, digital marketing, financial management, or HR solutions, we deliver turnkey solutions that let you focus on growth while we handle the complexity.",
  address: "#26 JAGADAM, MGHalli, K R PURAM, Bangalore 560016, India",
  phone: "+91-628-200-3636",
  phone2: "+91-628-201-3636",
  phone3: "+91-628-202-3636",
  email: "info@maroons.ai",
  social: {
    facebook: "https://facebook.com/maroonsai",
    instagram: "https://instagram.com/maroonsai",
    linkedin: "https://linkedin.com/company/maroonsai",
    twitter: "https://twitter.com/maroonsai",
  },
};
